'use client';

import { useState } from 'react';
import { useReveal } from '@/hooks/use-reveal';
import { BookOpen, ChevronDown, Feather } from 'lucide-react';

interface Term {
  term: string;
  pronunciation: string;
  category: 'cuir' | 'geste' | 'finition';
  shortDef: string;
  longDef: string;
  origin?: string;
}

const TERMS: Term[] = [
  {
    term: 'Pleine Fleur',
    pronunciation: '/plɛn flœʁ/',
    category: 'cuir',
    shortDef: "Le grain supérieur de la peau, le plus noble.",
    longDef:
      "Issu de la couche externe de la peau, juste sous le poil, le cuir pleine fleur conserve le grain naturel de l'animal. Aucune correction, aucun ponçage. C'est la partie la plus résistante, la plus belle, la plus rare — moins de 5 % des cuirs mondiaux méritent cette appellation. ASMAO n'utilise que la pleine fleur, sélectionnée à la main.",
    origin: "Appellation réglementée par la Chambre Syndicale des Tanneurs.",
  },
  {
    term: 'Point Sellier',
    pronunciation: '/pwɛ̃ sɛ.je/',
    category: 'geste',
    shortDef: "Couture à la main, deux aiguilles, fil ciré.",
    longDef:
      "Le point sellier est l'ossature invisible de toute pièce de maroquinerie noble. Deux aiguilles, un fil de lin ciré à la cire d'abeille, huit points au centimètre — ni un de plus, ni un de moins. Chaque point s'enroule, se tend, se pose. Une couture sellier ne se défait jamais : elle peut être coupée, arrachée, jamais défaite. C'est ce qui distingue un sac qui dure dix ans d'un sac qui dure cent ans.",
    origin: "Hérité de la sellerie équestre, XVIIIe siècle.",
  },
  {
    term: 'Patine',
    pronunciation: '/pa.tin/',
    category: 'finition',
    shortDef: "La couleur vivante, posée main, couches successives.",
    longDef:
      "La patine n'est pas une teinture : c'est une scénographie. Au pinceau large, l'artisan dépose quatre à six couches successives de pigments naturels, laissant le cuir boire chaque couche avant la suivante. Le résultat n'est jamais uniforme — il vit, il évolue, il vieillit. Un cuir patiné main devient plus beau avec les années, là où un cuir teint en machine se dégrade.",
    origin: "Technique développée dans les ateliers parisiens du Marais, XIXe siècle.",
  },
  {
    term: 'Tannage Végétal',
    pronunciation: '/ta.ɲaʒ ve.ʒe.tal/',
    category: 'cuir',
    shortDef: "Tannage lent, écorces, mimosas, châtaignier.",
    longDef:
      "Le tannage végétal est la méthode la plus ancienne et la plus respectueuse. Les peaux baignent de 30 à 60 jours dans des bains successifs d'écorces, de mimosas, de châtaignier, de quebracho. Aucun chrome, aucun accélérateur chimique. Le cuir qui en sort est ferme, dense, parfumé — il développe une patine magnifique et reste biodégradable. C'est le choix d'ASMAO pour 80 % de ses créations.",
    origin: "Méthode antique, perfectionnée en Toscane au Moyen-Âge.",
  },
  {
    term: 'Bord Franc',
    pronunciation: '/bɔʁ fʁɑ̃/',
    category: 'finition',
    shortDef: "Tranche teinte, cirée, polie à l'agate.",
    longDef:
      "Le bord franc est la signature d'une pièce cousue main. Plutôt que de retourner les coutures à l'intérieur, l'artisan laisse les bords apparents, les teinte à la main, applique une cire d'abeille, puis polit à la pierre d'agate jusqu'à obtenir un lustre parfait. C'est un travail minutieux, qui prend jusqu'à deux heures par tranche. Aucune machine ne peut le reproduire.",
    origin: "Spécificité des ateliers de sellier, jamais industrialisée.",
  },
  {
    term: 'Refente',
    pronunciation: '/ʁə.fɑ̃t/',
    category: 'geste',
    shortDef: "Couper le cuir dans son épaisseur, au refendoir.",
    longDef:
      "Une peau de bovin adulte peut atteindre 6 millimètres d'épaisseur. Pour la rendre utilisable, l'artisan la refend — c'est-à-dire qu'il la coupe horizontalement en deux ou trois couches. Seule la fleur (couche supérieure) devient cuir pleine fleur. Les couches inférieures, dites croûton, deviendront du cuir corrigé ou du cuir vernis. La refente se fait encore à la main pour les peaux de luxe, au refendoir à tranchant.",
    origin: "Geste d'atelier, transmis de maître à apprenti.",
  },
  {
    term: 'Vegetal Calf',
    pronunciation: '/ve.ʒe.tal kaf/',
    category: 'cuir',
    shortDef: "Veau tanné végétal, grain serré, fleur soyeuse.",
    longDef:
      "Le veau est le roi des cuirs : grain serré, fleur soyeuse, résistance exceptionnelle pour une douceur incomparable. Tanné végétal, il développe une patine rapide et profonde. ASMAO sélectionne ses veaux chez trois tanneries françaises et italiennes, tous issus d'élevages laitiers — aucune peau n'est prélevée pour le cuir seul. Chaque peau est numérotée et tracée.",
    origin: "Sélection ASMAO, tanneries du Puy-en-Velay et de Toscane.",
  },
  {
    term: 'Glacé Main',
    pronunciation: '/ɡla.se mɛ̃/',
    category: 'finition',
    shortDef: "Finition brillante, pierres d'agate, mouchoir de soie.",
    longDef:
      "Le glacé main est l'ultime finition d'un cuir noble. Après la patine, après la cire, l'artisan frotte la surface avec une pierre d'agate polie, puis avec un mouchoir de soie. La friction échauffe la cire, qui pénètre le cuir en profondeur. Le grain se resserre, le brillant devient miroir. C'est le geste le plus patient du métier : trois heures de polissage pour une seule face de sac.",
    origin: "Tradition orfèvre, transposée au cuir au XIXe siècle.",
  },
];

const CATEGORY_LABELS = {
  cuir: 'Matière',
  geste: 'Geste',
  finition: 'Finition',
} as const;

const CATEGORY_COLORS = {
  cuir: '#C9A961',
  geste: '#8B4513',
  finition: '#5C2E03',
} as const;

export default function Lexique() {
  const { ref, visible } = useReveal();
  const [openIdx, setOpenIdx] = useState<number | null>(0);

  return (
    <section
      id="lexique"
      ref={ref}
      className="relative py-28 md:py-40 bg-[#F8F1E4] overflow-hidden"
    >
      <div
        className="absolute inset-0 opacity-[0.025] pointer-events-none"
        style={{
          backgroundImage:
            'radial-gradient(circle at 1px 1px, rgba(43,24,16,0.6) 1px, transparent 0)',
          backgroundSize: '36px 36px',
        }}
      />
      <div
        className="absolute inset-0 opacity-30 pointer-events-none"
        style={{
          backgroundImage:
            'radial-gradient(ellipse at 80% 20%, rgba(201, 169, 97, 0.12) 0%, transparent 50%)',
        }}
      />

      <div className="container mx-auto px-6 relative">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.6fr] gap-12 lg:gap-20 max-w-7xl mx-auto">
          <div className="lg:sticky lg:top-32 lg:self-start">
            <div className={`reveal ${visible ? 'visible' : ''} mb-6`}>
              <div className="flex items-center gap-4">
                <div className="w-10 h-px bg-[#8B4513]" />
                <span className="text-[#8B4513] font-serif-display text-sm tracking-[0.4em] uppercase">
                  Le Lexique
                </span>
              </div>
            </div>

            <h2
              className={`reveal ${visible ? 'visible' : ''} font-display text-[#2B1810] text-4xl md:text-5xl lg:text-6xl font-medium leading-[1.05] mb-8`}
            >
              Les Mots
              <br />
              <span className="italic text-luxe-gold">du Métier</span>
            </h2>

            <p
              className={`reveal ${visible ? 'visible' : ''} text-[#5C2E03]/80 text-lg font-serif-display font-light leading-relaxed mb-8`}
            >
              Le cuir parle une langue précise, héritière de quatre siècles de pratique. Connaître
              ces mots, c'est comprendre ce qui distingue une pièce exceptionnelle d'une pièce
              ordinaire — et reconnaître, sous le verbe, la main de l'artisan.
            </p>

            <div
              className={`reveal ${visible ? 'visible' : ''} flex items-start gap-4 p-6 bg-[#2B1810]/5 border-l-2 border-[#C9A961]`}
            >
              <Feather size={22} className="text-[#8B4513] shrink-0 mt-1" strokeWidth={1.25} />
              <div>
                <div className="text-[#2B1810] font-display text-sm tracking-wide mb-2">
                  Le Savoir se Transmet
                </div>
                <p className="text-[#5C2E03]/70 text-sm font-serif-display italic leading-relaxed">
                  Chacun de ces termes est un fragment d'un héritage vivant. L'artisan ne les
                  apprend pas dans un livre : il les entend, les répète, les incarne — jusqu'à
                  ce qu'ils deviennent des gestes.
                </p>
              </div>
            </div>

            <div className={`reveal ${visible ? 'visible' : ''} mt-8 flex flex-wrap gap-4`}>
              {(Object.keys(CATEGORY_LABELS) as Array<keyof typeof CATEGORY_LABELS>).map((cat) => (
                <div key={cat} className="flex items-center gap-2">
                  <div
                    className="w-2.5 h-2.5 rounded-full"
                    style={{ backgroundColor: CATEGORY_COLORS[cat] }}
                  />
                  <span className="text-[#5C2E03]/70 text-[10px] tracking-[0.25em] uppercase font-serif-display">
                    {CATEGORY_LABELS[cat]}
                  </span>
                </div>
              ))}
            </div>
          </div>

          <div className="space-y-3">
            {TERMS.map((t, idx) => {
              const isOpen = openIdx === idx;
              return (
                <div
                  key={t.term}
                  className={`reveal ${visible ? 'visible' : ''} border border-[#8B4513]/15 bg-white/60 backdrop-blur-sm transition-all duration-500 ${
                    isOpen ? 'shadow-[0_12px_40px_-12px_rgba(43,24,16,0.2)]' : 'hover:border-[#C9A961]/50'
                  }`}
                  style={{ transitionDelay: `${idx * 60}ms` }}
                >
                  <button
                    onClick={() => setOpenIdx(isOpen ? null : idx)}
                    className="w-full text-left p-6 md:p-7 flex items-center gap-5 group"
                  >
                    <div
                      className="shrink-0 w-3 h-3 rounded-full transition-all duration-500"
                      style={{
                        backgroundColor: CATEGORY_COLORS[t.category],
                        boxShadow: isOpen ? `0 0 0 4px ${CATEGORY_COLORS[t.category]}25` : 'none',
                      }}
                    />

                    <div className="flex-1">
                      <div className="flex items-baseline gap-3 flex-wrap">
                        <h3
                          className={`font-display text-xl md:text-2xl tracking-wide transition-colors duration-500 ${
                            isOpen ? 'text-[#2B1810]' : 'text-[#2B1810]/85 group-hover:text-[#2B1810]'
                          }`}
                        >
                          {t.term}
                        </h3>
                        <span className="text-[#8B4513]/60 text-xs font-serif-display italic">
                          {t.pronunciation}
                        </span>
                      </div>
                      <div
                        className={`text-[#5C2E03]/70 text-sm font-serif-display mt-1.5 transition-all duration-500 ${
                          isOpen ? 'opacity-0 h-0 mt-0 overflow-hidden' : 'opacity-100'
                        }`}
                      >
                        {t.shortDef}
                      </div>
                    </div>

                    <div className="hidden md:block shrink-0">
                      <span
                        className="text-[10px] tracking-[0.25em] uppercase font-serif-display px-3 py-1.5 border"
                        style={{
                          color: CATEGORY_COLORS[t.category],
                          borderColor: `${CATEGORY_COLORS[t.category]}40`,
                          backgroundColor: `${CATEGORY_COLORS[t.category]}08`,
                        }}
                      >
                        {CATEGORY_LABELS[t.category]}
                      </span>
                    </div>

                    <ChevronDown
                      size={20}
                      className={`shrink-0 text-[#8B4513] transition-transform duration-500 ${
                        isOpen ? 'rotate-180' : ''
                      }`}
                    />
                  </button>

                  <div
                    className={`grid transition-all duration-500 ease-out ${
                      isOpen ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'
                    }`}
                  >
                    <div className="overflow-hidden">
                      <div className="px-6 md:px-7 pb-7 pt-2">
                        <div className="border-t border-[#8B4513]/15 pt-5">
                          <p className="text-[#2B1810]/85 text-base font-serif-display font-light leading-relaxed mb-4">
                            {t.longDef}
                          </p>
                          {t.origin && (
                            <div className="flex items-start gap-2.5 mt-5">
                              <BookOpen size={14} className="text-[#C9A961] shrink-0 mt-0.5" strokeWidth={1.5} />
                              <p className="text-[#5C2E03]/60 text-xs font-serif-display italic leading-relaxed">
                                {t.origin}
                              </p>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}

            <div className={`reveal ${visible ? 'visible' : ''} pt-6 text-center`}>
              <div className="divider-gold w-24 mx-auto mb-5" />
              <p className="text-[#5C2E03]/70 text-sm font-serif-display italic">
                Huit mots, huit portes. Le reste s'apprend en atelier.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
